import JIL_CONSTANTS from 'api/jil/JilConstants';
import {MEMBER_EVENT, MODEL_CACHE_ID} from 'models/member/MemberConstants';
import eventBus from 'services/events/eventBus';
import feature from 'services/feature';
import log from 'services/log';
import {getListCacheKey} from 'utils/cacheUtils';

import JilModelList from '../modelList/JilModelList';

class MemberList extends JilModelList {
  /**
   * @class
   * @param {Object}[options] - See JilModelList for additional options
   * @param {String} [options.cacheClearingEvents] - The events which cause the cache to be cleared.
   *   In most cases, the defaults should be used.
   * @param {String} [options.filterExcludeDomain] - domain to exclude from results
   * @param {String} [options.filterIncludeDomain] - domain to include in results
   * @param {Boolean} [options.isCacheable] - Are entries cached to avoid unnecessary queries? (defaults to true)
   * @param {String} [options.orgId] - Org ID
   * @param {Function} [options.refreshResource] - A function that returns the list items.
   * @param {Function} [options.saveResource] - A function that saves the list items.
   */
  // eslint-disable-next-line complexity -- Complexity will reduce after temp_src2_cache is removed
  constructor(options) {
    if (feature.isEnabled('temp_src2_cache') && !options.orgId) {
      throw new Error('MemberList.constructor() requires orgId');
    }

    super({
      cacheClearingEvents: options.cacheClearingEvents || [
        MEMBER_EVENT.CREATE,
        MEMBER_EVENT.DELETE,
        MEMBER_EVENT.UPDATE,
      ],
      filterQuery: options.filterQuery,
      isCacheable: options.isCacheable ?? true, // hardcode to true when temp_src2_cache removed
      itemClassRef: options.itemClassRef,
      items: options.items,
      modelCacheId: options.modelCacheId || MODEL_CACHE_ID,
      pageNumber: options.pageNumber,
      pageSize: options.pageSize,
      resource: options.refreshResource,
      sortExpression: options.sortExpression || JIL_CONSTANTS.SORT.FNAME_LNAME,
      sortOrder: options.sortOrder || JIL_CONSTANTS.ORDER.ASC,
      transformResponseData: options.transformResponseData,
    });

    this.orgId = options.orgId;
    this.saveResource = options.saveResource;
    if (feature.isEnabled('temp_src2_cache')) {
      this.filter.excludeDomain = options?.filterExcludeDomain ?? false;
      this.filter.includeDomain = options?.filterIncludeDomain ?? false;
    } else {
      this.filter.excludeDomain = options.filterExcludeDomain;
      this.filter.includeDomain = options.filterIncludeDomain;
    }
  }

  /**
   * @description Method to return the current unique key for this list.
   *              Used for caching the fetched response.
   *
   * @param {Object} params - params for refreshResource -- can be removed with temp_src2_cache
   * @returns {String} key to uniquely identify this list
   */
  getKey(params = {}) {
    if (feature.isDisabled('temp_src2_cache')) {
      return super.getKey(params);
    }

    return getListCacheKey(this.getQueryParams());
  }

  /**
   * @description Get params for api call to fetch list.
   *              Subclasses should add additional params as needed.
   *
   * @returns {Object} params to be passed to query
   */
  getQueryParams() {
    return {
      currentPage: this.pagination.currentPage,
      filterExcludeDomain: this.filter.excludeDomain,
      filterIncludeDomain: this.filter.includeDomain,
      filterQuery: this.filter.query,
      orgId: this.orgId,
      page_size: this.pagination.pageSize,
      sort: this.sort.expression,
      sort_order: this.sort.order,
    };
  }

  /**
   * @description Method to return the save operations. If desired, this can be overriden.
   */
  getSaveOperations(options) {
    return getSaveOperations(this, options);
  }

  /**
   * @description Method to execute after a successful refresh.  Overriden where necessary by subclasses.
   */
  // eslint-disable-next-line class-methods-use-this -- may be overridden by subclasses
  onRefreshSuccess() {}

  /**
   * @description Method to refresh the contents of the member list
   * @param {Object} [queryParams] - params to pass into the GET -- can be removed with temp_src2_cache
   * @throws {Error} - If refresh fails
   * @throws {Error} - If onRefreshSuccess fails
   * @returns {Promise<MemberList>} promise - resolved when the list is refreshed
   */
  async refresh(queryParams = {}) {
    if (feature.isDisabled('temp_src2_cache')) {
      return super.refresh({
        filterExcludeDomain: this.filter.excludeDomain,
        filterIncludeDomain: this.filter.includeDomain,
        ...queryParams,
      });
    }

    try {
      await super.refresh(this.getQueryParams());
    } catch (error) {
      log.error('MemberList.refresh() failed. Error: ', error);
      throw error;
    }

    try {
      await this.onRefreshSuccess();
    } catch (error) {
      log.error(`${this.constructor.name}.onRefreshSuccess() failed. Error: `, error);
      throw error;
    }
    return this;
  }

  /**
   * @description Method to reset the contents of the member list
   * @returns {Promise<MemberList>} promise - resolved with the instance
   */
  reset() {
    this.filter.lastQuery = '';
    this.filter.query = '';

    this.items = [];

    this.pagination.currentPage = 1;
    this.pagination.itemCount = 0;

    this.sort.expression = '';
    this.sort.order = '';

    return Promise.resolve(this);
  }

  /**
   * @description Method to save the contents of the member list
   * @param {Object} [params] - params to pass into the save resource
   * @param {Object} [options] - custom options for saving
   * @param {Boolean} [options.refresh] - whether to refresh after saving, defaults to true.
   * @param {Object} [options.removePathOverride] - A map from user id -> patch_operation, used to
   *   override the default patch remove operations.
   *   This assumes a 1-to-1 mapping between the user id and the patch operation. If a user id can map to more
   *   than one operation consider overriding the getSaveOperations mathod.
   * @returns {Promise<MemberList>} promise - resolved when the list is refreshed
   */
  async save(params = {}, options = {}) {
    if (!this.hasUnsavedChanges()) {
      return this;
    }
    const refresh = options.refresh === undefined ? true : options.refresh;
    const operations = this.getSaveOperations(options);
    try {
      await this.saveResource({
        operations,
        ...(feature.isEnabled('temp_src2_cache') ? {orgId: this.orgId} : {}),
        ...params,
      });
    } catch (error) {
      log.error('MemberList.save() failed. Error: ', error);
      return Promise.reject(error);
    }

    this.addedItems.forEach((member) => {
      eventBus.emit(MEMBER_EVENT.UPDATE, member.id);
    });
    this.resetAddedItems();

    this.removedItems.forEach((member) => {
      eventBus.emit(MEMBER_EVENT.UPDATE, member.id);
    });
    this.resetRemovedItems();

    this.filter.lastQuery = this.filter.query;
    this.filter.query = '';
    if (refresh) {
      try {
        await this.refresh();
      } catch (error) {
        log.error('MemberList.save() refresh failed. Error: ', error);
        return Promise.reject(error);
      }
    }
    return this;
  }
}

function getSaveOperations(model, options) {
  const addOperations = model.addedItems.map((member) => ({
    op: 'add',
    path: `/${member.id}`,
  }));
  const removeOperations = getRemoveOperations(model.removedItems, options.removePathOverride);
  return [...addOperations, ...removeOperations];
}

function getRemoveOperations(removedItems, pathOverride = {}) {
  return removedItems.map((member) => ({
    op: 'remove',
    path: pathOverride?.[member.id] || `/${member.id}`,
  }));
}

export default MemberList;
